import React, { useEffect, useMemo, useRef, useState } from 'react';

import { mockData } from '../mock';

const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));

function useScrollProgress() {
  const ref = useRef(null);
  const [state, setState] = useState({ local: 0, seen: 0, text: 0, fade: 1 });

  useEffect(() => {
    const update = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const local = clamp((vh - rect.top) / (vh + rect.height));
      const seen = clamp((vh - rect.top) / (vh * 0.72));
      const text = clamp((local - 0.18) / 0.24);
      const fade = clamp(1 - Math.max(0, local - 0.64) / 0.22);
      setState({ local, seen, text, fade });
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return [ref, state];
}

function Reveal({ children, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.18 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`eos-experience-reveal ${visible ? 'is-on' : ''} ${className}`}>
      {children}
    </div>
  );
}

function IntroSection() {
  const [ref, progress] = useScrollProgress();
  const introOpacity = clamp(progress.seen * progress.fade);
  const introY = (1 - progress.seen) * 54 - (1 - progress.fade) * 54;
  const introScale = 0.96 + progress.seen * 0.04;

  return (
    <section
      id="about"
      ref={ref}
      data-bg="light"
      className="eos-experience-intro"
      style={{ '--intro-opacity': introOpacity, '--intro-y': `${introY}px`, '--intro-scale': introScale }}
    >
      <div className="eos-experience-intro-inner">
        <h2>Elektrotechnik und Energie als durchdachtes System.</h2>
        <p>
          EOS Energy Systems verbindet Elektroinstallation, Photovoltaik,
          Speicher, Wallbox und Sicherheitstechnik zu Lösungen, die sauber
          geplant, fachgerecht umgesetzt und langfristig betreibbar sind.
        </p>
        <span>Planung · Umsetzung · Prüfung · Betrieb</span>
      </div>
    </section>
  );
}

function ServiceLeadScene({ service }) {
  const [ref, progress] = useScrollProgress();
  const stageIn = clamp(progress.local / 0.08);
  const grow = clamp((progress.local - 0.02) / 0.48);
  const textIn = clamp((progress.local - 0.45) / 0.1);
  const whiteStage = 1 - clamp((grow - 0.92) / 0.08);

  return (
    <section
      ref={ref}
      data-bg="light"
      className="eos-service-lead"
      style={{
        '--service-stage-in': stageIn,
        '--service-grow': grow,
        '--service-text-in': textIn,
        '--service-white-stage': whiteStage,
      }}
      aria-label="Leistungsbereich Photovoltaik"
    >
      <div className="eos-service-sticky">
        <div className="eos-service-stage">
          <figure className="eos-service-zoom-card">
            <img src={service.image} alt={service.alt} />
          </figure>
          <div className="eos-service-lead-copy">
            <span>{service.eyebrow}</span>
            <h2>{service.title}</h2>
            <p>{service.text}</p>
            <div className="eos-experience-specs">
              {service.stats.map(([value, label]) => (
                <div className="eos-experience-spec" key={`${service.id}-${value}-${label}`}>
                  <b>{value}</b>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServiceDetailScene({ service }) {
  const [ref, progress] = useScrollProgress();
  const imageProgress = clamp((progress.local - 0.08) / 0.14);

  return (
    <article
      id={service.id}
      ref={ref}
      className={`eos-service-detail ${service.align === 'right' ? 'is-right' : ''}`}
      style={{ '--text-progress': progress.text, '--local': progress.local, '--image-progress': imageProgress }}
    >
      <div className="eos-service-detail-bg" aria-hidden="true">
        <img src={service.image} alt={service.alt} />
      </div>
      <div className="eos-service-detail-copy">
        <div className="eos-experience-eyebrow">{service.eyebrow}</div>
        <h3>{service.title}</h3>
        <p>{service.text}</p>
        <div className="eos-experience-specs">
          {service.stats.map(([value, label]) => (
            <div className="eos-experience-spec" key={`${service.id}-${value}-${label}`}>
              <b>{value}</b>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
}

function ServicesSection() {
  const [pvService, ...moreServices] = mockData.serviceScenes;

  return (
    <section id="features" className="eos-services" aria-label="Leistungen von EOS Energy Systems">
      <ServiceLeadScene service={pvService} />
      <div className="eos-services-dark" data-bg="dark">
        {moreServices.map((service) => (
          <ServiceDetailScene key={service.id} service={service} />
        ))}
      </div>
    </section>
  );
}

function ProjectReferences() {
  const projects = useMemo(() => (mockData.projects || []).slice(0, 8), []);
  const projectCardsRef = useRef(null);
  const projectCardAnchorRef = useRef(null);
  const projectCardDragRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    startLeft: 0,
    pointerId: null,
    suppressClick: false,
    suppressClickTimer: 0,
  });
  const scrollSnapStateRef = useRef({
    lastSnapAt: 0,
    lastScrollY: 0,
    lastDistance: null,
    snappingUntil: 0,
    ticking: false,
    touching: false,
    releasedAt: 0,
    settleTimer: 0,
    forceAlign: false,
    allowMobileSnap: false,
  });
  const [activeId, setActiveId] = useState(projects[0]?.id);
  const [activeImage, setActiveImage] = useState({ projectId: projects[0]?.id, src: projects[0]?.images?.[0] });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [expandedMobileProject, setExpandedMobileProject] = useState(null);
  const [mobileProjectImageIndex, setMobileProjectImageIndex] = useState({});
  const activeProject = projects.find((project) => project.id === activeId) || projects[0];

  useEffect(() => {
    const el = projectCardsRef.current;
    if (!el) return;
    const frame = requestAnimationFrame(() => {
      const setWidth = el.querySelector('.eos-project-card-set')?.getBoundingClientRect().width || 0;
      if (setWidth > 0) el.scrollLeft = setWidth;
    });
    return () => cancelAnimationFrame(frame);
  }, [projects]);

  useEffect(() => {
    const el = projectCardsRef.current;
    if (!el) return undefined;

    let frame = 0;
    let settleTimer = 0;
    let wrapTimer = 0;

    const jumpProjectScrollInstantly = (left) => {
      const previousBehavior = el.style.scrollBehavior;
      const previousSnapType = el.style.scrollSnapType;
      el.style.scrollBehavior = 'auto';
      el.style.scrollSnapType = 'none';
      el.scrollLeft = left;
      requestAnimationFrame(() => {
        el.style.scrollBehavior = previousBehavior;
        el.style.scrollSnapType = previousSnapType;
      });
    };

    const wrapProjectScroll = () => {
      frame = 0;
      const setWidth = el.querySelector('.eos-project-card-set')?.getBoundingClientRect().width || 0;
      if (!setWidth) return;

      const lowerLimit = setWidth * 0.45;
      const upperLimit = setWidth * 1.55;

      if (el.scrollLeft < lowerLimit) {
        el.scrollLeft += setWidth;
      } else if (el.scrollLeft > upperLimit) {
        el.scrollLeft -= setWidth;
      }
    };

    const settleProjectScroll = () => {
      const setWidth = el.querySelector('.eos-project-card-set')?.getBoundingClientRect().width || 0;
      if (!setWidth || window.innerWidth > 760) return;
      window.clearTimeout(wrapTimer);

      const cardWidth = Math.max(1, window.innerWidth);
      const currentLeft = el.scrollLeft;
      const snappedLeft = Math.round(currentLeft / cardWidth) * cardWidth;

      if (snappedLeft < setWidth || snappedLeft >= setWidth * 2) {
        const direction = snappedLeft < setWidth ? 1 : -1;
        const wrappedLeft = snappedLeft + (direction * setWidth);
        jumpProjectScrollInstantly(wrappedLeft);
        return;
      }

      if (Math.abs(snappedLeft - currentLeft) > 1) {
        el.scrollTo({ left: snappedLeft, behavior: 'smooth' });
      }
    };

    const onProjectScroll = () => {
      const isMobile = window.innerWidth <= 760;
      if (!isMobile && !frame) {
        frame = requestAnimationFrame(wrapProjectScroll);
      }
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(settleProjectScroll, 360);
    };

    const drag = projectCardDragRef.current;
    const endDrag = (event) => {
      if (!drag.active) return;
      if (drag.moved) {
        drag.suppressClick = true;
        window.clearTimeout(drag.suppressClickTimer);
        drag.suppressClickTimer = window.setTimeout(() => { drag.suppressClick = false; }, 320);
      }
      if (drag.pointerId !== null && el.releasePointerCapture) {
        try { el.releasePointerCapture(drag.pointerId); } catch (_) {}
      }
      drag.active = false;
      drag.pointerId = null;
      el.classList.remove('is-pointer-dragging');
      el.style.scrollBehavior = '';
      if (event?.type === 'mouseleave') return;
      if (!frame) frame = requestAnimationFrame(wrapProjectScroll);
    };

    const onPointerDown = (event) => {
      if (window.innerWidth <= 760 || event.button !== 0) return;
      drag.active = true;
      drag.moved = false;
      drag.startX = event.clientX;
      drag.startLeft = el.scrollLeft;
      drag.pointerId = event.pointerId;
      el.style.scrollBehavior = 'auto';
    };

    const onPointerMove = (event) => {
      if (!drag.active || drag.pointerId !== event.pointerId) return;
      const dx = event.clientX - drag.startX;
      if (!drag.moved && Math.abs(dx) < 10) return;
      event.preventDefault();
      if (!drag.moved && el.setPointerCapture) {
        try { el.setPointerCapture(event.pointerId); } catch (_) {}
      }
      drag.moved = true;
      el.classList.add('is-pointer-dragging');
      el.scrollLeft = drag.startLeft - dx;
      if (!frame) frame = requestAnimationFrame(wrapProjectScroll);
    };

    const onPointerUp = (event) => {
      if (drag.pointerId !== event.pointerId) return;
      if (!drag.moved) {
        const card = event.target.closest('.eos-project-card');
        const projectId = card?.getAttribute('data-project-id');
        const project = projects.find((item) => String(item.id) === String(projectId));
        if (project && card?.getAttribute('tabindex') === '0') {
          setExpandedMobileProject(null);
          setActiveId(project.id);
          setActiveImage({ projectId: project.id, src: project.images?.[0] });
        }
      }
      endDrag(event);
    };

    const onNativeDragStart = (event) => {
      if (window.innerWidth > 760) event.preventDefault();
    };

    el.addEventListener('scroll', onProjectScroll, { passive: true });
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove, { passive: false });
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);
    el.addEventListener('mouseleave', endDrag);
    el.addEventListener('dragstart', onNativeDragStart);
    return () => {
      el.removeEventListener('scroll', onProjectScroll);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
      el.removeEventListener('mouseleave', endDrag);
      el.removeEventListener('dragstart', onNativeDragStart);
      endDrag();
      window.clearTimeout(drag.suppressClickTimer);
      window.clearTimeout(settleTimer);
      window.clearTimeout(wrapTimer);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [projects]);

  useEffect(() => {
    const anchor = projectCardAnchorRef.current;
    if (!anchor) return undefined;

    const state = scrollSnapStateRef.current;
    state.lastScrollY = window.scrollY;

    const requestAlignment = (delay = 0) => {
      window.clearTimeout(state.settleTimer);
      state.settleTimer = window.setTimeout(() => {
        if (state.ticking) return;
        state.forceAlign = true;
        state.ticking = true;
        requestAnimationFrame(tryAlignProjectCards);
      }, delay);
    };

    const markTouchStart = () => {
      state.touching = true;
      state.allowMobileSnap = false;
      window.clearTimeout(state.settleTimer);
    };

    const markTouchMove = () => {
      state.touching = true;
      state.allowMobileSnap = false;
      state.lastScrollY = window.scrollY;
      window.clearTimeout(state.settleTimer);
    };

    const markTouchEnd = () => {
      state.touching = false;
      state.allowMobileSnap = true;
      state.releasedAt = performance.now();
      state.lastScrollY = window.scrollY;
      requestAlignment(220);
    };

    const markTouchCancel = () => {
      state.touching = false;
      state.allowMobileSnap = false;
      state.releasedAt = 0;
      state.lastScrollY = window.scrollY;
      window.clearTimeout(state.settleTimer);
    };

    const tryAlignProjectCards = () => {
      state.ticking = false;

      const now = performance.now();
      if (now < state.snappingUntil) return;
      if (now - state.lastSnapAt < 1200) return;

      const currentScrollY = window.scrollY;
      const scrollDelta = currentScrollY - state.lastScrollY;
      state.lastScrollY = currentScrollY;
      const forcedAlignment = state.forceAlign;
      state.forceAlign = false;

      if (!forcedAlignment && (Math.abs(scrollDelta) < 2 || Math.abs(scrollDelta) > 180)) return;

      const isMobile = window.innerWidth <= 760;
      const target = isMobile
        ? anchor.querySelector('.eos-project-card-viewport') || anchor
        : anchor;
      const rect = target.getBoundingClientRect();

      if (isMobile) {
        if (state.touching || !state.allowMobileSnap || now - state.releasedAt < 180) return;

        const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
        const visibleRatio = visibleHeight / Math.min(rect.height || window.innerHeight, window.innerHeight);
        const targetTop = Math.max(0, (window.innerHeight - rect.height) / 2);
        const distanceToTargetTop = rect.top - targetTop;
        const previousDistance = state.lastDistance;
        state.lastDistance = distanceToTargetTop;

        const isAlmostFullscreen = visibleRatio >= 0.88;
        const isNearCentered = Math.abs(distanceToTargetTop) < 96;
        const crossesTarget =
          forcedAlignment ||
          previousDistance === null ||
          Math.sign(previousDistance) !== Math.sign(distanceToTargetTop) ||
          Math.abs(distanceToTargetTop) < Math.abs(previousDistance);

        if (!isAlmostFullscreen || !isNearCentered || !crossesTarget) return;

        state.lastSnapAt = now;
        state.snappingUntil = now + 560;
        state.allowMobileSnap = false;
        window.__eosSuppressMobileHeaderUntil = now + 1000;
        window.dispatchEvent(new CustomEvent('eos:mobile-reference-magnet'));
        window.scrollBy({ top: distanceToTargetTop, behavior: 'smooth' });

        window.setTimeout(() => {
          window.__eosSuppressMobileHeaderUntil = performance.now() + 420;
          const currentRect = target.getBoundingClientRect();
          const currentTargetTop = Math.max(0, (window.innerHeight - currentRect.height) / 2);
          const currentDistance = currentRect.top - currentTargetTop;
          if (Math.abs(currentDistance) > 2 && Math.abs(currentDistance) < 34) {
            window.scrollBy({ top: currentDistance, behavior: 'auto' });
            state.lastDistance = 0;
          }
        }, 380);
        return;
      }

      const header = document.querySelector('.eos-experience-topbar');
      const media = document.querySelector('.eos-project-feature-media-wrap');
      if (!media) return;

      const desktopBottomGap = 16;
      const desktopTopGap = 14;
      const headerBottom = header ? header.getBoundingClientRect().bottom : 72;
      const targetMediaTop = headerBottom + desktopTopGap;
      const mediaRect = media.getBoundingClientRect();
      const distanceToMediaTop = mediaRect.top - targetMediaTop;
      const distanceToViewportBottom = rect.bottom - (window.innerHeight - desktopBottomGap);
      const previousDistance = state.lastDistance;
      state.lastDistance = distanceToViewportBottom;

      if (previousDistance !== null && Math.abs(distanceToMediaTop) < 4) return;

      const isNearBottomLine = Math.abs(distanceToViewportBottom) < 120;
      const isNearTopLine = Math.abs(distanceToMediaTop) < 130;
      const crossesBottomLine =
        forcedAlignment ||
        previousDistance === null ||
        (scrollDelta > 0 && previousDistance > 8 && distanceToViewportBottom < 120) ||
        (scrollDelta < 0 && previousDistance < -8 && distanceToViewportBottom > -120);
      const anchorIsInUsefulRange = mediaRect.top > -90 && mediaRect.top < window.innerHeight * 0.45 && rect.bottom < window.innerHeight + 180;

      if ((!isNearBottomLine && !isNearTopLine) || !crossesBottomLine || !anchorIsInUsefulRange) return;

      state.lastSnapAt = now;
      state.snappingUntil = now + 520;
      window.scrollBy({ top: distanceToMediaTop, behavior: 'smooth' });

      window.setTimeout(() => {
        const currentMediaRect = media.getBoundingClientRect();
        const currentHeaderBottom = header ? header.getBoundingClientRect().bottom : 72;
        const currentDistance = currentMediaRect.top - (currentHeaderBottom + desktopTopGap);
        if (Math.abs(currentDistance) > 2 && Math.abs(currentDistance) < 42) {
          window.scrollBy({ top: currentDistance, behavior: 'auto' });
          state.lastDistance = 0;
        }
      }, 360);
    };

    const onScroll = () => {
      if (window.innerWidth <= 760 && state.touching) {
        state.lastScrollY = window.scrollY;
        return;
      }
      if (state.ticking) return;
      state.ticking = true;
      requestAnimationFrame(tryAlignProjectCards);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('touchstart', markTouchStart, { passive: true });
    window.addEventListener('touchmove', markTouchMove, { passive: true });
    window.addEventListener('touchend', markTouchEnd, { passive: true });
    window.addEventListener('touchcancel', markTouchCancel, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('touchstart', markTouchStart);
      window.removeEventListener('touchmove', markTouchMove);
      window.removeEventListener('touchend', markTouchEnd);
      window.removeEventListener('touchcancel', markTouchCancel);
      window.clearTimeout(state.settleTimer);
    };
  }, []);

  if (!activeProject) return null;

  const galleryImages = activeProject.images || [];
  const coverImage = activeImage.projectId === activeProject.id ? activeImage.src : galleryImages[0];
  const coverIndex = Math.max(0, galleryImages.indexOf(coverImage));


  const showImage = (image) => setActiveImage({ projectId: activeProject.id, src: image });
  const stepImage = (direction) => {
    if (!galleryImages.length) return;
    const nextIndex = (coverIndex + direction + galleryImages.length) % galleryImages.length;
    showImage(galleryImages[nextIndex]);
  };
  const selectProject = (project) => {
    setActiveId(project.id);
    const imageIndex = mobileProjectImageIndex[project.id] || 0;
    setActiveImage({ projectId: project.id, src: project.images?.[imageIndex] || project.images?.[0] });
  };
  const stepMobileProjectImage = (project, direction, event) => {
    event.preventDefault();
    event.stopPropagation();
    const images = project.images || [];
    if (images.length < 2) return;
    setMobileProjectImageIndex((current) => {
      const currentIndex = current[project.id] || 0;
      const nextIndex = (currentIndex + direction + images.length) % images.length;
      setActiveId(project.id);
      setActiveImage({ projectId: project.id, src: images[nextIndex] });
      return { ...current, [project.id]: nextIndex };
    });
  };
  const handleProjectCardClick = (project, event) => {
    if (projectCardDragRef.current.suppressClick) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event.target.closest('.eos-project-card-details')) {
      event.preventDefault();
      setExpandedMobileProject((current) => (current === project.id ? null : project.id));
      selectProject(project);
      return;
    }
    setExpandedMobileProject(null);
    selectProject(project);
  };
  const scrollProjectCards = (direction) => {
    const el = projectCardsRef.current;
    if (!el) return;
    const setWidth = el.querySelector('.eos-project-card-set')?.getBoundingClientRect().width || 0;
    const amount = Math.max(280, el.clientWidth * 0.78);

    if (setWidth > 0) {
      if (el.scrollLeft < setWidth * 0.55) el.scrollLeft += setWidth;
      if (el.scrollLeft > setWidth * 1.55) el.scrollLeft -= setWidth;
    }

    el.scrollBy({ left: direction * amount, behavior: 'smooth' });

    window.setTimeout(() => {
      if (!setWidth) return;
      if (el.scrollLeft < setWidth * 0.55) el.scrollLeft += setWidth;
      if (el.scrollLeft > setWidth * 1.55) el.scrollLeft -= setWidth;
    }, 520);
  };

  return (
    <section id="projects" data-bg="light" className="eos-experience-tiles">
      <div className="eos-experience-tiles-head">
        <Reveal>
          <span className="eos-experience-section-kicker">Referenzen</span>
          <h2>Projekte, die man ansehen kann.</h2>
        </Reveal>
        <Reveal>
          <p>
            Eine Auswahl echter Projekte aus Photovoltaik, Speicher, Wallbox und Elektrotechnik.
            Weitere Projekte lassen sich später einfach in der Mock-Datei ergänzen.
          </p>
        </Reveal>
      </div>

      <div className="eos-project-showcase">
        <Reveal className="eos-project-feature-reveal">
          <article className="eos-project-feature">
            <div className="eos-project-feature-media-wrap">
              <button
                type="button"
                className="eos-project-feature-media"
                onClick={() => setLightboxOpen(true)}
                aria-label={`${activeProject.title} Bild groß öffnen`}
              >
                <img src={coverImage} alt={`${activeProject.title}: ${activeProject.headerdescription}`} />
                <div className="eos-project-feature-badge">Bild {coverIndex + 1} / {galleryImages.length}</div>
                <div className="eos-project-zoom-hint">Bild vergrößern</div>
              </button>
              {galleryImages.length > 1 && (
                <>
                  <button type="button" className="eos-project-feature-image-nav is-left" onClick={() => stepImage(-1)} aria-label="Vorheriges Projektbild">‹</button>
                  <button type="button" className="eos-project-feature-image-nav is-right" onClick={() => stepImage(1)} aria-label="Nächstes Projektbild">›</button>
                </>
              )}
            </div>

            <div className="eos-project-feature-copy">
              <span>{activeProject.title}</span>
              <h3>{activeProject.headerdescription}</h3>
              <p>{activeProject.description}</p>
            </div>

            <div className="eos-project-highlights">
              {activeProject.highlights?.slice(0, 3).map((item) => (
                <div key={`${activeProject.id}-${item.heading}`}>
                  <b>{item.text}</b>
                  <small>{item.heading}</small>
                </div>
              ))}
            </div>
          </article>

          <div ref={projectCardAnchorRef} className="eos-project-card-carousel" aria-label="Referenzprojekte auswählen">
            <button type="button" className="eos-project-card-nav is-left" onClick={() => scrollProjectCards(-1)} aria-label="Projektkarten nach links schieben">‹</button>
            <div ref={projectCardsRef} className="eos-project-card-viewport">
              <div className="eos-project-card-track">
                {[0, 1, 2].map((setIndex) => (
                  <div className="eos-project-card-set" key={`project-card-set-${setIndex}`} aria-hidden={setIndex !== 1}>
                    {projects.map((project) => {
                      const isActive = project.id === activeProject.id;
                      const isExpanded = expandedMobileProject === project.id;
                      const cardImageIndex = Math.min(mobileProjectImageIndex[project.id] || 0, Math.max(0, (project.images?.length || 1) - 1));
                      const cardImage = project.images?.[cardImageIndex] || project.images?.[0];
                      const hasMultipleImages = (project.images?.length || 0) > 1;
                      return (
                        <button
                          key={`${project.id}-${setIndex}`}
                          type="button"
                          className={`eos-project-card ${isActive ? 'is-active' : ''} ${isExpanded ? 'is-mobile-expanded' : ''}`}
                          onClick={(event) => handleProjectCardClick(project, event)}
                          onPointerUpCapture={() => {
                            if (window.innerWidth > 760 && !projectCardDragRef.current.moved) {
                              setExpandedMobileProject(null);
                              selectProject(project);
                            }
                          }}
                          data-project-id={project.id}
                          aria-pressed={isActive}
                          aria-expanded={isExpanded}
                          tabIndex={setIndex === 1 ? 0 : -1}
                        >
                          <img draggable="false" src={cardImage} alt={`${project.title}: ${project.headerdescription}`} />
                          {hasMultipleImages && (
                            <>
                              <span className="eos-project-card-image-position" aria-hidden="true">{cardImageIndex + 1} / {project.images.length}</span>
                              <span
                                role="button"
                                tabIndex={setIndex === 1 ? 0 : -1}
                                className="eos-project-card-image-step is-left"
                                aria-label={`Vorheriges Bild zu ${project.title}`}
                                onClick={(event) => stepMobileProjectImage(project, -1, event)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') stepMobileProjectImage(project, -1, event);
                                }}
                              >‹</span>
                              <span
                                role="button"
                                tabIndex={setIndex === 1 ? 0 : -1}
                                className="eos-project-card-image-step is-right"
                                aria-label={`Nächstes Bild zu ${project.title}`}
                                onClick={(event) => stepMobileProjectImage(project, 1, event)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') stepMobileProjectImage(project, 1, event);
                                }}
                              >›</span>
                            </>
                          )}
                          <span className="eos-project-card-label">{project.title}</span>
                          <strong>{project.headerdescription}</strong>
                          <span className="eos-project-card-highlights">
                            {project.highlights?.slice(0, 3).map((item) => (
                              <span key={`${project.id}-${item.heading}`}>
                                <b>{item.text}</b>
                                <small>{item.heading}</small>
                              </span>
                            ))}
                          </span>
                          <p className="eos-project-card-description">{project.description}</p>
                          <span className="eos-project-card-details">Details</span>
                          <small className="eos-project-card-image-count">{project.images?.length || 0} Bilder ansehen</small>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
            <button type="button" className="eos-project-card-nav is-right" onClick={() => scrollProjectCards(1)} aria-label="Projektkarten nach rechts schieben">›</button>
          </div>
        </Reveal>

      </div>

      {lightboxOpen && (
        <div className="eos-project-lightbox" role="dialog" aria-modal="true" aria-label={`${activeProject.title} Bildgalerie`}>
          <button type="button" className="eos-project-lightbox-close" onClick={() => setLightboxOpen(false)} aria-label="Galerie schließen">×</button>
          <button type="button" className="eos-project-lightbox-arrow is-left" onClick={() => stepImage(-1)} aria-label="Vorheriges Bild">‹</button>
          <img src={coverImage} alt={`${activeProject.title} Referenzbild groß`} />
          <button type="button" className="eos-project-lightbox-arrow is-right" onClick={() => stepImage(1)} aria-label="Nächstes Bild">›</button>
          <div className="eos-project-lightbox-caption">{activeProject.title} · Bild {coverIndex + 1} von {galleryImages.length}</div>
        </div>
      )}
    </section>
  );
}

export default function EosExperienceSections({ scrollToSection }) {
  return (
    <>
      <IntroSection />

      <ServicesSection />

      <ProjectReferences />

      <section id="contact-teaser" data-bg="light" className="eos-experience-outro">
        <Reveal>
          <h2>Starten wir mit einem klaren Gespräch.</h2>
          <p>
            Wir klären Anforderungen, Bestand, Energiebedarf und Zielgrößen –
            damit aus der ersten Idee eine belastbare technische Lösung wird.
          </p>
          <button type="button" className="eos-experience-pill is-blue" onClick={() => scrollToSection?.('contact')}>
            Kontakt aufnehmen
          </button>
        </Reveal>
      </section>
    </>
  );
}
